import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  Engineering,
  Analytics,
  CloudSync,
  Security,
  Speed,
  TrendingUp,
  Dashboard,
  SmartToy,
  Menu as MenuIcon,
  Close,
  ArrowForward,
  PlayCircleOutline,
  CheckCircle,
  Star,
  Monitor,
  Cloud,
  Shield,
  Speed as SpeedIcon,
  Psychology,
  Memory,
  AutoAwesome,
  Science,
  Biotech,
  PrecisionManufacturing,
  Timeline,
  NetworkCheck,
  Visibility,
  DataUsage,
  SettingsInputComponent,
} from '@mui/icons-material';

interface HeroPageProps {
  onGetStarted: () => void;
}

const HeroPage: React.FC<HeroPageProps> = ({ onGetStarted }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigationItems = [
    { label: 'Overview', href: '#overview' },
    { label: 'Features', href: '#features' },
    { label: 'Technology', href: '#technology' },
    { label: 'Solutions', href: '#solutions' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Support', href: '#support' },
  ];

  const products = [
    {
      title: 'Digital Twin 4.0 Enterprise',
      subtitle: 'Quantum Intelligence. Infinite Possibilities.',
      description: 'Harness the power of quantum computing and advanced AI to create the most sophisticated digital twin ecosystem ever built. Experience the future of industrial intelligence.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&h=800&fit=crop&crop=center&auto=format&q=80',
      features: ['Quantum AI Analytics', 'Neural Network Processing', 'Holographic Visualization', 'Zero-Latency Sync', 'Quantum Security', 'Multi-Dimensional Modeling'],
      price: 'Starting at $2,999/month',
      badge: 'Quantum Edition',
    },
    {
      title: 'Digital Twin 4.0 Neural',
      subtitle: 'Think. Learn. Evolve.',
      description: 'Self-evolving digital twins that learn and adapt in real-time. Powered by advanced neural networks and machine learning algorithms that continuously optimize performance.',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&h=800&fit=crop&crop=center&auto=format&q=80',
      features: ['Neural Learning', 'Adaptive Intelligence', 'Predictive Evolution', 'Cognitive Analytics', 'Autonomous Optimization', 'Smart Integration'],
      price: 'Starting at $1,499/month',
      badge: 'AI Powered',
    },
    {
      title: 'Digital Twin 4.0 Core',
      subtitle: 'Foundation of the Future.',
      description: 'The essential digital twin platform that transforms your industrial operations into intelligent, connected systems. Built for the next generation of smart manufacturing.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop&crop=center&auto=format&q=80',
      features: ['Real-time Intelligence', 'IoT Integration', 'Predictive Maintenance', 'Performance Analytics', 'Smart Alerts', 'Cloud Native'],
      price: 'Starting at $799/month',
      badge: 'Future Ready',
    },
  ];

  const features = [
    {
      icon: <Visibility sx={{ fontSize: 32 }} />,
      title: 'Holographic Visualization',
      description: 'Experience your industrial assets in stunning 3D holographic displays with real-time data overlays and immersive analytics.',
    },
    {
      icon: <Science sx={{ fontSize: 32 }} />,
      title: 'Quantum Cloud Integration',
      description: 'Seamlessly connect with quantum computing networks and advanced cloud infrastructure for unlimited processing power.',
    },
    {
      icon: <Security sx={{ fontSize: 32 }} />,
      title: 'Quantum Encryption',
      description: 'Protect your data with unbreakable quantum encryption and advanced cybersecurity protocols from the future.',
    },
    {
      icon: <Psychology sx={{ fontSize: 32 }} />,
      title: 'Neural Processing',
      description: 'Process infinite data streams with our quantum neural networks that think and learn at the speed of light.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000000', color: '#ffffff' }}>
      {/* Apple-style Navigation */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, maxWidth: 1024, mx: 'auto', width: '100%' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: '#007AFF',
                mr: 2,
              }}
            >
              <SmartToy sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.125rem',
                letterSpacing: '-0.02em',
                color: '#ffffff',
              }}
            >
              Digital Twin 4.0
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.label}
                  sx={{
                    color: '#ffffff',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    px: 2,
                    '&:hover': {
                      color: '#007AFF',
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: '#ffffff' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            bgcolor: '#000000',
            width: 280,
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#ffffff' }}>
              <Close />
            </IconButton>
          </Box>
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.label} sx={{ px: 0, py: 1 }}>
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#ffffff',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ pt: '60px' }}>
        {/* Futuristic Hero Section */}
        <Box sx={{ 
          position: 'relative',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 25%, #1a1a2e 50%, #16213e 75%, #000000 100%)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(0, 122, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(52, 199, 89, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(255, 149, 0, 0.1) 0%, transparent 50%)',
            animation: 'pulse 4s ease-in-out infinite alternate',
          },
          '@keyframes pulse': {
            '0%': { opacity: 0.3 },
            '100%': { opacity: 0.7 },
          },
        }}>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
            <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 1000 }}>
              <Fade in timeout={1000}>
                <Box sx={{ mb: 6 }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '3.5rem', md: '7rem' },
                      fontWeight: 100,
                      lineHeight: 0.85,
                      mb: 6,
                      letterSpacing: '-0.08em',
                      fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                      background: 'linear-gradient(135deg, #00D4FF 0%, #007AFF 25%, #34C759 50%, #FF9500 75%, #AF52DE 100%)',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: '0 0 40px rgba(0, 212, 255, 0.6)',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '300px',
                        height: '3px',
                        background: 'linear-gradient(90deg, transparent, #00D4FF, transparent)',
                      },
                    }}
                  >
                    DIGITAL TWIN 4.0
                  </Typography>
                </Box>
              </Fade>
              
              <Fade in timeout={1500}>
                <Box sx={{ mb: 8 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.75rem', md: '3rem' },
                      fontWeight: 200,
                      color: '#ffffff',
                      mb: 4,
                      maxWidth: 1000,
                      mx: 'auto',
                      lineHeight: 1.1,
                      fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                      letterSpacing: '0.02em',
                      textShadow: '0 0 30px rgba(255, 255, 255, 0.2)',
                      textTransform: 'uppercase',
                    }}
                  >
                    The Future of Industrial Intelligence
                  </Typography>
                </Box>
              </Fade>
              
              <Fade in timeout={2000}>
                <Box sx={{ mb: 10 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#b3b3b3',
                      fontWeight: 300,
                      lineHeight: 1.8,
                      maxWidth: 800,
                      mx: 'auto',
                      fontSize: { xs: '1.125rem', md: '1.5rem' },
                      fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                      letterSpacing: '0.01em',
                      textAlign: 'center',
                    }}
                  >
                    Experience the next generation of digital twins powered by quantum computing, 
                    neural networks, and holographic visualization technology.
                  </Typography>
                </Box>
              </Fade>
              
              <Fade in timeout={2500}>
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onGetStarted}
                    startIcon={<AutoAwesome sx={{ fontSize: 24 }} />}
                    sx={{
                      bgcolor: 'transparent',
                      color: '#00D4FF',
                      border: '2px solid #00D4FF',
                      px: 8,
                      py: 3,
                      fontSize: '1.25rem',
                      fontWeight: 300,
                      fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                      borderRadius: '60px',
                      textTransform: 'none',
                      letterSpacing: '0.05em',
                      boxShadow: '0 0 40px rgba(0, 212, 255, 0.4)',
                      minWidth: 220,
                      '&:hover': {
                        bgcolor: '#00D4FF',
                        color: '#000000',
                        boxShadow: '0 0 60px rgba(0, 212, 255, 0.8)',
                        transform: 'translateY(-3px)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    Enter the Future
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayCircleOutline sx={{ fontSize: 24 }} />}
                    sx={{
                      borderColor: '#34C759',
                      color: '#34C759',
                      px: 8,
                      py: 3,
                      fontSize: '1.25rem',
                      fontWeight: 300,
                      fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                      borderRadius: '60px',
                      textTransform: 'none',
                      letterSpacing: '0.05em',
                      boxShadow: '0 0 30px rgba(52, 199, 89, 0.3)',
                      minWidth: 220,
                      '&:hover': {
                        bgcolor: '#34C759',
                        color: '#000000',
                        boxShadow: '0 0 50px rgba(52, 199, 89, 0.6)',
                        transform: 'translateY(-3px)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    Watch Demo
                  </Button>
                </Box>
              </Fade>
            </Box>
          </Container>
        </Box>

        {/* Hero Product Showcase */}
        {products.map((product, index) => (
          <Box key={index} sx={{ 
            py: 12, 
            bgcolor: index % 2 === 0 ? '#000000' : '#0a0a0a',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: index % 2 === 0 
                ? 'linear-gradient(45deg, rgba(0, 122, 255, 0.05) 0%, transparent 50%)'
                : 'linear-gradient(45deg, rgba(52, 199, 89, 0.05) 0%, transparent 50%)',
            },
          }}>
            <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center', mb: 8, width: '100%', maxWidth: 1200 }}>
                {/* Futuristic Badge */}
                {product.badge && (
                  <Fade in timeout={800 + index * 200}>
                    <Box sx={{ mb: 3 }}>
                      <Chip
                        label={product.badge}
                        sx={{
                          bgcolor: 'transparent',
                          color: index === 0 ? '#007AFF' : index === 1 ? '#34C759' : '#FF9500',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          px: 3,
                          py: 1,
                          borderRadius: '25px',
                          border: `2px solid ${index === 0 ? '#007AFF' : index === 1 ? '#34C759' : '#FF9500'}`,
                          boxShadow: `0 0 20px ${index === 0 ? 'rgba(0, 122, 255, 0.3)' : index === 1 ? 'rgba(52, 199, 89, 0.3)' : 'rgba(255, 149, 0, 0.3)'}`,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                        }}
                      />
                    </Box>
                  </Fade>
                )}
                
                <Fade in timeout={1000 + index * 200}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.75rem', md: '4.5rem' },
                      fontWeight: 200,
                      lineHeight: 1.0,
                      mb: 3,
                      letterSpacing: '-0.05em',
                      fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                      background: index === 0 
                        ? 'linear-gradient(135deg, #00D4FF, #007AFF)'
                        : index === 1 
                        ? 'linear-gradient(135deg, #34C759, #00FF88)'
                        : 'linear-gradient(135deg, #FF9500, #FF6B35)',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: `0 0 40px ${index === 0 ? 'rgba(0, 212, 255, 0.6)' : index === 1 ? 'rgba(52, 199, 89, 0.6)' : 'rgba(255, 149, 0, 0.6)'}`,
                      textTransform: 'uppercase',
                    }}
                  >
                    {product.title}
                  </Typography>
                </Fade>
                
                <Fade in timeout={1200 + index * 200}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: '1.375rem', md: '1.875rem' },
                      fontWeight: 200,
                      color: '#ffffff',
                      mb: 5,
                      maxWidth: 800,
                      mx: 'auto',
                      lineHeight: 1.2,
                      fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                      letterSpacing: '0.02em',
                      textShadow: '0 0 25px rgba(255, 255, 255, 0.15)',
                      textAlign: 'center',
                    }}
                  >
                    {product.subtitle}
                  </Typography>
                </Fade>

                <Fade in timeout={1400 + index * 200}>
                  <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: 'transparent',
                        color: index === 0 ? '#00D4FF' : index === 1 ? '#34C759' : '#FF9500',
                        border: `2px solid ${index === 0 ? '#00D4FF' : index === 1 ? '#34C759' : '#FF9500'}`,
                        px: 6,
                        py: 2,
                        fontSize: '1.125rem',
                        fontWeight: 300,
                        fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                        borderRadius: '50px',
                        textTransform: 'none',
                        letterSpacing: '0.03em',
                        boxShadow: `0 0 25px ${index === 0 ? 'rgba(0, 212, 255, 0.3)' : index === 1 ? 'rgba(52, 199, 89, 0.3)' : 'rgba(255, 149, 0, 0.3)'}`,
                        '&:hover': {
                          bgcolor: index === 0 ? '#00D4FF' : index === 1 ? '#34C759' : '#FF9500',
                          color: '#000000',
                          boxShadow: `0 0 35px ${index === 0 ? 'rgba(0, 212, 255, 0.5)' : index === 1 ? 'rgba(52, 199, 89, 0.5)' : 'rgba(255, 149, 0, 0.5)'}`,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </Fade>
              </Box>

              {/* Futuristic Product Image */}
              <Fade in timeout={1600 + index * 200}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    mb: 6,
                    boxShadow: `0 30px 60px ${index === 0 ? 'rgba(0, 122, 255, 0.3)' : index === 1 ? 'rgba(52, 199, 89, 0.3)' : 'rgba(255, 149, 0, 0.3)'}`,
                    border: `2px solid ${index === 0 ? '#007AFF' : index === 1 ? '#34C759' : '#FF9500'}`,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-10px',
                      left: '-10px',
                      right: '-10px',
                      bottom: '-10px',
                      background: index === 0 
                        ? 'linear-gradient(45deg, rgba(0, 122, 255, 0.3), rgba(0, 212, 255, 0.3))'
                        : index === 1 
                        ? 'linear-gradient(45deg, rgba(52, 199, 89, 0.3), rgba(0, 255, 136, 0.3))'
                        : 'linear-gradient(45deg, rgba(255, 149, 0, 0.3), rgba(255, 107, 53, 0.3))',
                      borderRadius: 4,
                      filter: 'blur(15px)',
                      zIndex: -1,
                    },
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: `0 40px 80px ${index === 0 ? 'rgba(0, 122, 255, 0.5)' : index === 1 ? 'rgba(52, 199, 89, 0.5)' : 'rgba(255, 149, 0, 0.5)'}`,
                    },
                    transition: 'all 0.4s ease',
                  }}
                >
                  <img
                    src={product.image}
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '700px',
                      objectFit: 'cover',
                      filter: 'brightness(1.1) contrast(1.1)',
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      setImageErrors(prev => ({ ...prev, [index]: true }));
                      
                      // Use a fallback image
                      if (!imageErrors[index]) {
                        target.src = `https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&h=800&fit=crop&crop=center&auto=format&q=80&t=${Date.now()}`;
                      } else {
                        // Final fallback to a placeholder SVG
                        target.src = `data:image/svg+xml;base64,${btoa(`
                          <svg width="1200" height="800" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="1200" height="800" fill="#111111"/>
                            <rect x="50" y="50" width="1100" height="700" stroke="${index === 0 ? '#00D4FF' : index === 1 ? '#34C759' : '#FF9500'}" stroke-width="4" fill="none"/>
                            <text x="600" y="350" fill="#ffffff" font-family="Inter, sans-serif" font-size="48" font-weight="300" text-anchor="middle">${product.title}</text>
                            <text x="600" y="450" fill="#b3b3b3" font-family="Inter, sans-serif" font-size="24" font-weight="200" text-anchor="middle">${product.subtitle}</text>
                          </svg>
                        `)}`;
                      }
                    }}
                    onLoad={() => {
                      setImageErrors(prev => ({ ...prev, [index]: false }));
                    }}
                  />
                </Box>
              </Fade>

              {/* Product Features */}
              <Fade in timeout={1800 + index * 200}>
                <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#b3b3b3',
                      mb: 4,
                      fontWeight: 300,
                      lineHeight: 1.7,
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                      letterSpacing: '0.01em',
                      maxWidth: 800,
                      mx: 'auto',
                    }}
                  >
                    {product.description}
                  </Typography>
                  
                  <Grid container spacing={3} sx={{ mb: 6, justifyContent: 'center' }}>
                    {product.features.map((feature, featureIndex) => (
                      <Grid item xs={12} sm={6} md={4} key={featureIndex} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          gap: 2,
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '20px',
                          px: 3,
                          py: 2,
                          minWidth: 200,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}>
                          <CheckCircle sx={{ fontSize: 20, color: index === 0 ? '#00D4FF' : index === 1 ? '#34C759' : '#FF9500' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#ffffff',
                              fontSize: '0.875rem',
                              fontWeight: 400,
                              textAlign: 'center',
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#007AFF',
                      fontWeight: 600,
                      fontSize: '1.125rem',
                    }}
                  >
                    {product.price}
                  </Typography>
                </Box>
              </Fade>
            </Container>
          </Box>
        ))}

        {/* Futuristic Features Section */}
        <Box sx={{ 
          bgcolor: '#0a0a0a', 
          py: 12,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(0, 122, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(52, 199, 89, 0.1) 0%, transparent 50%)',
          },
        }}>
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center', mb: 12, width: '100%', maxWidth: 1000 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.75rem', md: '4.5rem' },
                  fontWeight: 200,
                  mb: 6,
                  letterSpacing: '-0.05em',
                  fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
                  background: 'linear-gradient(135deg, #00D4FF, #34C759, #FF9500)',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 40px rgba(0, 212, 255, 0.4)',
                  textTransform: 'uppercase',
                }}
              >
                Next-Generation Technology
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#a1a1aa',
                  maxWidth: 800,
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.375rem' },
                  lineHeight: 1.7,
                  fontWeight: 300,
                  letterSpacing: '0.01em',
                }}
              >
                Experience the future of industrial intelligence with cutting-edge quantum computing, 
                neural networks, and holographic visualization.
              </Typography>
            </Box>

            <Grid container spacing={6} sx={{ justifyContent: 'center' }}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Card
                    sx={{
                      height: '100%',
                      bgcolor: 'transparent',
                      border: `2px solid ${index === 0 ? '#007AFF' : index === 1 ? '#34C759' : index === 2 ? '#FF9500' : '#AF52DE'}`,
                      borderRadius: 3,
                      textAlign: 'center',
                      p: 4,
                      transition: 'all 0.4s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: index === 0 
                          ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.1), rgba(0, 212, 255, 0.05))'
                          : index === 1 
                          ? 'linear-gradient(135deg, rgba(52, 199, 89, 0.1), rgba(0, 255, 136, 0.05))'
                          : index === 2 
                          ? 'linear-gradient(135deg, rgba(255, 149, 0, 0.1), rgba(255, 107, 53, 0.05))'
                          : 'linear-gradient(135deg, rgba(175, 82, 222, 0.1), rgba(255, 0, 255, 0.05))',
                        zIndex: -1,
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: `0 25px 50px ${index === 0 ? 'rgba(0, 122, 255, 0.3)' : index === 1 ? 'rgba(52, 199, 89, 0.3)' : index === 2 ? 'rgba(255, 149, 0, 0.3)' : 'rgba(175, 82, 222, 0.3)'}`,
                        borderColor: index === 0 ? '#00D4FF' : index === 1 ? '#00FF88' : index === 2 ? '#FF6B35' : '#FF00FF',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: 'transparent',
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        border: `2px solid ${index === 0 ? '#007AFF' : index === 1 ? '#34C759' : index === 2 ? '#FF9500' : '#AF52DE'}`,
                        filter: `drop-shadow(0 0 10px ${index === 0 ? '#007AFF' : index === 1 ? '#34C759' : index === 2 ? '#FF9500' : '#AF52DE'})`,
                      }}
                    >
                      <Box sx={{ 
                        color: index === 0 ? '#007AFF' : index === 1 ? '#34C759' : index === 2 ? '#FF9500' : '#AF52DE',
                        fontSize: 32,
                      }}>
                        {feature.icon}
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 300,
                        mb: 2,
                        fontSize: '1.375rem',
                        fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                        letterSpacing: '0.02em',
                        textShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#b3b3b3',
                        lineHeight: 1.7,
                        fontSize: '1rem',
                        fontFamily: '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
                        letterSpacing: '0.01em',
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Statistics Section */}
        <Box sx={{ bgcolor: '#f5f5f7', py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 600,
                  mb: 3,
                  letterSpacing: '-0.02em',
                  color: '#1d1d1f',
                }}
              >
                Trusted by industry leaders worldwide
              </Typography>
            </Box>
            
            <Grid container spacing={4} sx={{ mb: 8 }}>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      color: '#007AFF',
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' },
                    }}
                  >
                    10,000+
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#86868b',
                      fontWeight: 400,
                      fontSize: '1rem',
                    }}
                  >
                    Assets Monitored
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      color: '#34C759',
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' },
                    }}
                  >
                    99.2%
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#86868b',
                      fontWeight: 400,
                      fontSize: '1rem',
                    }}
                  >
                    Uptime Achieved
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      color: '#FF9500',
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' },
                    }}
                  >
                    85%
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#86868b',
                      fontWeight: 400,
                      fontSize: '1rem',
                    }}
                  >
                    Cost Reduction
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      color: '#AF52DE',
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '2rem', md: '3rem' },
                    }}
                  >
                    $2.5M
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#86868b',
                      fontWeight: 400,
                      fontSize: '1rem',
                    }}
                  >
                    Average Savings
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ bgcolor: '#ffffff', py: 8, textAlign: 'center' }}>
          <Container maxWidth="md">
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 600,
                mb: 3,
                letterSpacing: '-0.02em',
                color: '#1d1d1f',
              }}
            >
              Ready to revolutionize your operations?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#86868b',
                mb: 6,
                fontWeight: 400,
                lineHeight: 1.5,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Join thousands of forward-thinking companies using Digital Twin 4.0 to achieve unprecedented operational efficiency and intelligence.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={onGetStarted}
                sx={{
                  bgcolor: '#007AFF',
                  color: '#ffffff',
                  px: 6,
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 400,
                  borderRadius: '980px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#0056CC',
                  },
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: '#007AFF',
                  color: '#007AFF',
                  px: 6,
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 400,
                  borderRadius: '980px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#0056CC',
                    color: '#0056CC',
                    bgcolor: 'rgba(0, 122, 255, 0.04)',
                  },
                }}
              >
                Schedule Demo
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#f5f5f7', py: 6, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#86868b',
                mb: 2,
                fontSize: '0.75rem',
              }}
            >
              IEEE Standard Compliant • Multi-Agent Architecture • Enterprise Ready
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#86868b',
                fontSize: '0.75rem',
              }}
            >
              © 2024 Digital Twin 4.0. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HeroPage;

